import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getProfile, patchProfile, listMyDesigns, requestVerification } from "../lib/architectPortal";
import { uploadFile } from "../lib/applications";
import ArchitectPortalSubnav from "../components/ArchitectPortalSubnav";
import BackBar from "../components/BackBar";
import { Toaster, toast } from "sonner";
import { assetUrl } from "../lib/format";
import { BadgeCheck, Upload, X, Plus, ArrowUpRight, ChevronUp, ChevronDown, ShieldCheck, ShieldAlert } from "lucide-react";
import {
  SERVICE_AREAS,
  ARCHITECT_TYPOLOGIES as TYPOLOGIES,
  DEMOGRAPHIC_FLAGS as FLAGS,
} from "../lib/architectTaxonomy";

export default function ProfileEditor() {
  const { user, refreshUser } = useRole();
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [myDesigns, setMyDesigns] = useState([]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getProfile(user.id)
      .then((p) => {
        if (!alive) return;
        setProfile(p);
        setDraft(toDraft(p));
      })
      .catch((e) => alive && setErr(e));
    // Designs feed the "Built from" picker on each highlight card.
    listMyDesigns(user.id)
      .then((d) => alive && setMyDesigns(d.items || []))
      .catch(() => alive && setMyDesigns([]));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (err) {
    return (
      <section data-testid="profile-editor-error">
        <ArchitectPortalSubnav />
        <p style={{ color: "#A23B2E" }}>Could not load your profile.</p>
      </section>
    );
  }
  if (!draft) {
    return (
      <section data-testid="profile-editor-loading">
        <ArchitectPortalSubnav />
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading your profile…
        </div>
      </section>
    );
  }

  function setField(field, value) {
    setDraft((d) => ({ ...d, [field]: value }));
  }
  function toggleArr(field, value) {
    setDraft((d) => {
      const arr = d[field] || [];
      const next = arr.includes(value)
        ? arr.filter((x) => x !== value)
        : [...arr, value];
      return { ...d, [field]: next };
    });
  }

  async function save() {
    setSaving(true);
    try {
      const fields = {
        practice_statement: draft.practice_statement,
        bio: draft.bio,
        photo: draft.photo,
        firm_photo: draft.firm_photo,
        service_areas: draft.service_areas,
        specialisations: draft.specialisations,
        demographic_flags: draft.demographic_flags,
        // Project-highlight CRUD is supported via this surface (Pass 2):
        project_highlights: (draft.project_highlights || []).map((h) => ({
          // Drop any UI-only keys before persisting.
          project_name: (h.project_name || "").trim(),
          neighborhood: (h.neighborhood || "").trim(),
          lot_dimensions: (h.lot_dimensions || "").trim(),
          narrative: (h.narrative || "").trim(),
          completion_year:
            h.completion_year === "" || h.completion_year == null
              ? null
              : Number(h.completion_year),
          client_type: (h.client_type || "").trim(),
          built_from_design_id: h.built_from_design_id || "",
          built_from_design_name: h.built_from_design_name || "",
          image_ref: h.image_ref || "",
          metrics: (h.metrics || [])
            .map((m) => ({
              label: (m.label || "").trim(),
              value: (m.value || "").trim(),
            }))
            .filter((m) => m.label || m.value),
        })).filter((h) => h.project_name),
        // Enriched profile fields (Prompt 3.1 follow-up):
        firm_tagline: draft.firm_tagline,
        philosophy_quote: draft.philosophy_quote,
        years_founded:
          draft.years_founded === "" || draft.years_founded == null
            ? null
            : Number(draft.years_founded),
        team_size:
          draft.team_size === "" || draft.team_size == null
            ? null
            : Number(draft.team_size),
        lifestyle_focuses: (draft.lifestyle_focuses || [])
          .map((s) => String(s).trim())
          .filter(Boolean),
        credentials: draft.credentials,
        education: draft.education,
        awards: draft.awards,
      };
      const updated = await patchProfile(user.id, fields);
      setProfile(updated);
      setDraft(toDraft(updated));
      toast.success("Profile updated.");
      await refreshUser();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="animate-fade-in-up"
      data-testid="profile-editor-page"
    >
      <Toaster position="top-right" richColors closeButton />
      <ArchitectPortalSubnav />

      <BackBar
        parent={{ to: "/architect", label: "Dashboard" }}
        trail={[
          { label: "Dashboard", to: "/architect" },
          { label: "Profile" },
        ]}
        testId="profile-editor-backbar"
      />

      <div className="flex items-center gap-3" style={{ marginTop: 18 }}>
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Profile
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontSize: "clamp(28px, 4vw, 36px)",
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        Your public profile.
      </h1>
      <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
        <p
          className="max-w-[640px]"
          style={{ color: "#6C6B85", fontSize: 14, lineHeight: 1.6, margin: 0 }}
        >
          Edit your public profile. Personal contact details are not editable —
          all contact runs through the platform.
        </p>
        {profile?.id && (
          <div className="flex items-center" style={{ gap: 14 }}>
            <Link
              to={`/architects/${profile.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="wn-btn wn-btn-tertiary inline-flex items-center"
              style={{ gap: 6, paddingLeft: 0 }}
              data-testid="profile-view-public"
            >
              View live profile <ArrowUpRight size={14} />
            </Link>
          </div>
        )}
      </div>

      {/* "Last admin update" trust banner */}
      {profile.last_admin_edit && (
        <LastAdminEditBanner edit={profile.last_admin_edit} />
      )}

      <div
        className="mt-7 grid gap-8 items-start"
        style={{ gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1fr)" }}
      >
        {/* Editor with tabs */}
        <div
          className="wn-card"
          style={{ padding: "22px 24px" }}
          data-testid="profile-editor"
        >
          <ProfileTabs
            draft={draft}
            setField={setField}
            toggleArr={toggleArr}
            profile={profile}
            myDesigns={myDesigns}
            user={user}
            setProfile={setProfile}
          />

          <div className="mt-7 flex items-center gap-3">
            <button
              className="wn-btn wn-btn-primary"
              onClick={save}
              disabled={saving}
              data-testid="profile-save-btn"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              className="wn-btn wn-btn-tertiary"
              onClick={() => setDraft(toDraft(profile))}
              disabled={saving}
              data-testid="profile-reset-btn"
            >
              Reset
            </button>
          </div>

          <div
            className="mt-5"
            style={{
              fontSize: 12,
              color: "#6C6B85",
              fontStyle: "italic",
              fontFamily: '"Newsreader", Georgia, serif',
            }}
          >
            Personal email, phone, and external links are intentionally not
            editable. Contact runs through the platform.
          </div>
        </div>

        {/* Preview */}
        <ProfilePreview draft={draft} />
      </div>
    </section>
  );
}

// ─── Tabbed editor body ───────────────────────────────────────────────────
const PROFILE_TABS = [
  { key: "identity", label: "Identity" },
  { key: "practice", label: "Practice" },
  { key: "capabilities", label: "Capabilities" },
  { key: "credentials", label: "Credentials" },
  { key: "portfolio", label: "Portfolio" },
];

function ProfileTabs({
  draft,
  setField,
  toggleArr,
  profile,
  myDesigns,
  user,
  setProfile,
}) {
  const [tab, setTab] = useState("identity");
  return (
    <div data-testid="profile-tabs">
      <div
        role="tablist"
        aria-label="Profile sections"
        style={{
          display: "flex",
          gap: 2,
          borderBottom: "1px solid #D9D8E4",
          marginBottom: 20,
          overflowX: "auto",
        }}
      >
        {PROFILE_TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              data-testid={`profile-tab-${t.key}`}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 16px",
                cursor: "pointer",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: active ? "#1B1F4B" : "#6C6B85",
                borderBottom: `2px solid ${active ? "#B14E2F" : "transparent"}`,
                marginBottom: -1,
                whiteSpace: "nowrap",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "identity" && (
        <IdentityTab draft={draft} setField={setField} toggleArr={toggleArr} />
      )}
      {tab === "practice" && (
        <PracticeTab draft={draft} setField={setField} />
      )}
      {tab === "capabilities" && (
        <CapabilitiesTab draft={draft} setField={setField} toggleArr={toggleArr} />
      )}
      {tab === "credentials" && (
        <CredentialsTab
          draft={draft}
          setField={setField}
          profile={profile}
          user={user}
          setProfile={setProfile}
        />
      )}
      {tab === "portfolio" && (
        <PortfolioTab
          draft={draft}
          setField={setField}
          myDesigns={myDesigns}
        />
      )}
    </div>
  );
}

function IdentityTab({ draft, setField, toggleArr }) {
  return (
    <div data-testid="profile-tab-identity-panel">
      <div>
        <div className="wn-label">Photograph</div>
        <PhotoEditor
          value={draft.photo}
          onChange={(p) => setField("photo", p)}
        />
      </div>

      <div className="mt-5" data-testid="profile-firm-photo-section">
        <div className="wn-label">Firm photograph (hero image)</div>
        <p style={{ color: "#6C6B85", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
          The larger photo shown on your public profile beside the "At a glance" card.
          Landscape orientation works best.
        </p>
        <PhotoEditor
          value={draft.firm_photo}
          onChange={(p) => setField("firm_photo", p)}
          testIdPrefix="profile-firm-photo"
          aspect="4 / 3"
          width={132}
        />
      </div>

      <div className="mt-6" data-testid="profile-tagline-section">
        <div className="wn-label">Firm tagline</div>
        <input
          className="wn-input mt-2"
          placeholder="A short sentence describing the practice's stance (5–10 words)."
          value={draft.firm_tagline}
          onChange={(e) => setField("firm_tagline", e.target.value)}
          maxLength={140}
          data-testid="profile-firm-tagline"
        />
        <div style={{ color: "#6C6B85", fontSize: 11, marginTop: 4 }}>
          <span className="tabular-nums">{(draft.firm_tagline || "").length}</span>/140
          characters. Shown beneath your firm name in the hero.
        </div>
      </div>

      <div className="mt-6">
        <div className="wn-label">Demographic flags (self-attested)</div>
        <ChipRow
          options={FLAGS.map((f) => ({ key: f, label: f }))}
          selected={draft.demographic_flags}
          onToggle={(v) => toggleArr("demographic_flags", v)}
          testidPrefix="profile-flag"
        />
      </div>
    </div>
  );
}

function PracticeTab({ draft, setField }) {
  return (
    <div data-testid="profile-tab-practice-panel">
      <div className="grid sm:grid-cols-2 gap-4" data-testid="profile-atglance-section">
        <div>
          <div className="wn-label">Year firm founded</div>
          <input
            className="wn-input mt-2 tabular-nums"
            inputMode="numeric"
            placeholder="e.g. 2012"
            value={draft.years_founded}
            onChange={(e) =>
              setField(
                "years_founded",
                e.target.value.replace(/[^\d]/g, "").slice(0, 4),
              )
            }
            data-testid="profile-years-founded"
          />
        </div>
        <div>
          <div className="wn-label">Practitioners (team size)</div>
          <input
            className="wn-input mt-2 tabular-nums"
            inputMode="numeric"
            placeholder="e.g. 6"
            value={draft.team_size}
            onChange={(e) =>
              setField(
                "team_size",
                e.target.value.replace(/[^\d]/g, "").slice(0, 3),
              )
            }
            data-testid="profile-team-size"
          />
        </div>
      </div>
      <div
        style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 6, fontStyle: "italic" }}
      >
        These figures power the "At a glance" card and the byline rail on
        your public profile.
      </div>

      <div className="mt-5">
        <div className="wn-label">Practice statement</div>
        <textarea
          className="wn-input mt-1"
          style={{ minHeight: 80, padding: 10, lineHeight: 1.5 }}
          value={draft.practice_statement || ""}
          onChange={(e) => setField("practice_statement", e.target.value)}
          data-testid="profile-practice-statement"
        />
      </div>

      <div className="mt-5">
        <div className="wn-label">Biography</div>
        <textarea
          className="wn-input mt-1"
          style={{ minHeight: 110, padding: 10, lineHeight: 1.5 }}
          value={draft.bio || ""}
          onChange={(e) => setField("bio", e.target.value)}
          data-testid="profile-bio"
        />
      </div>

      <div className="mt-5" data-testid="profile-quote-section">
        <div className="wn-label">Philosophy pull-quote</div>
        <textarea
          className="wn-input mt-2"
          placeholder="A standout pull-quote, 15–30 words."
          rows={3}
          value={draft.philosophy_quote}
          onChange={(e) => setField("philosophy_quote", e.target.value)}
          maxLength={280}
          data-testid="profile-philosophy-quote"
        />
        <div style={{ color: "#6C6B85", fontSize: 11, marginTop: 4 }}>
          <span className="tabular-nums">{(draft.philosophy_quote || "").length}</span>/280
          characters.
        </div>
      </div>
    </div>
  );
}

function CapabilitiesTab({ draft, setField, toggleArr }) {
  return (
    <div data-testid="profile-tab-capabilities-panel">
      <div>
        <div className="wn-label">Service areas</div>
        <ChipRow
          options={SERVICE_AREAS.map((s) => ({ key: s, label: s }))}
          selected={draft.service_areas}
          onToggle={(v) => toggleArr("service_areas", v)}
          testidPrefix="profile-area"
        />
      </div>

      <div className="mt-5">
        <div className="wn-label">Typology specialisations</div>
        <ChipRow
          options={TYPOLOGIES}
          selected={draft.specialisations}
          onToggle={(v) => toggleArr("specialisations", v)}
          testidPrefix="profile-typology"
        />
      </div>

      <div className="mt-5" data-testid="profile-lifestyle-section">
        <div className="wn-label">Who you work for (lifestyle focuses)</div>
        <p style={{ color: "#6C6B85", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
          Up to four short phrases describing the households or clients you build for.
        </p>
        <TagEditor
          tags={draft.lifestyle_focuses}
          onChange={(arr) => setField("lifestyle_focuses", arr)}
          placeholder="Add a focus (e.g. Aging-in-place residents)"
          max={4}
          testid="profile-lifestyle"
        />
      </div>
    </div>
  );
}

function CredentialsTab({ draft, setField, profile, user, setProfile }) {
  return (
    <div data-testid="profile-tab-credentials-panel">
      <VerificationRequiredFields
        profile={profile}
        onSubmitted={async () => {
          const next = await getProfile(user.id);
          setProfile(next);
        }}
      />
      <div className="mt-5" data-testid="credentials-editor-section">
        <p
          style={{
            color: "#6C6B85",
            fontSize: 12.5,
            lineHeight: 1.5,
            marginTop: 0,
          }}
        >
          These surface in the Credentials &amp; education rail on your public
          profile. Year is shown beside each entry in tabular figures.
        </p>
        <CredentialsBlockEditor
          label="Credentials"
          rows={draft.credentials}
          schema={[
            { key: "abbr", placeholder: "AIA", grow: 1 },
            { key: "year", placeholder: "Year", grow: 1, type: "number", tabular: true },
            { key: "name", placeholder: "Full credential name (e.g. American Institute of Architects, member)", grow: 4 },
          ]}
          onChange={(arr) => setField("credentials", arr)}
          emptyTemplate={{ abbr: "", year: "", name: "" }}
          testid="credentials"
        />
        <CredentialsBlockEditor
          label="Education"
          rows={draft.education}
          schema={[
            { key: "degree", placeholder: "M.Arch", grow: 2 },
            { key: "year", placeholder: "Year", grow: 1, type: "number", tabular: true },
            { key: "institution", placeholder: "Institution", grow: 4 },
          ]}
          onChange={(arr) => setField("education", arr)}
          emptyTemplate={{ degree: "", year: "", institution: "" }}
          testid="education"
        />
        <CredentialsBlockEditor
          label="Recognition (awards)"
          rows={draft.awards}
          schema={[
            { key: "name", placeholder: "Award name", grow: 3 },
            { key: "year", placeholder: "Year", grow: 1, type: "number", tabular: true },
            { key: "body", placeholder: "Awarding body", grow: 3 },
            { key: "description", placeholder: "Short description (~30 words)", grow: 6, multiline: true },
          ]}
          onChange={(arr) => setField("awards", arr)}
          emptyTemplate={{ name: "", year: "", body: "", description: "" }}
          testid="awards"
        />
      </div>
    </div>
  );
}

function PortfolioTab({ draft, setField, myDesigns }) {
  return (
    <div data-testid="profile-tab-portfolio-panel">
      <div data-testid="highlights-editor-section">
        <div className="wn-label">Completed-project highlights</div>
        <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
          Case studies of projects you've completed. Each becomes a card in
          the "Completed projects" rail of your public profile.
        </p>
        <HighlightsEditor
          highlights={draft.project_highlights}
          onChange={(arr) => setField("project_highlights", arr)}
          designs={myDesigns}
        />
      </div>
    </div>
  );
}

function toDraft(p) {
  return {
    photo: p.photo || "",
    firm_photo: p.firm_photo || "",
    practice_statement: p.practice_statement || "",
    bio: p.bio || "",
    service_areas: p.service_areas || [],
    specialisations: p.specialisations || [],
    demographic_flags: p.demographic_flags || [],
    project_highlights: p.project_highlights || [],
    firm_tagline: p.firm_tagline || "",
    philosophy_quote: p.philosophy_quote || "",
    years_founded: p.years_founded == null ? "" : String(p.years_founded),
    team_size: p.team_size == null ? "" : String(p.team_size),
    lifestyle_focuses: (p.lifestyle_focuses || []).slice(),
    credentials: (p.credentials || []).map((c) => ({ ...c })),
    education: (p.education || []).map((e) => ({ ...e })),
    awards: (p.awards || []).map((a) => ({ ...a })),
  };
}

function ProfilePreview({ draft }) {
  const photo = draft.photo ? assetUrl(draft.photo) : null;
  const firmPhoto = draft.firm_photo ? assetUrl(draft.firm_photo) : null;
  return (
    <div
      className="lg:sticky"
      style={{ top: 16, alignSelf: "start" }}
      data-testid="profile-preview"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="wn-label" style={{ color: "#6C6B85" }}>
          Live preview
        </span>
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 11.5,
          }}
        >
          Updates as you type
        </span>
      </div>
      <div
        className="wn-card"
        style={{ padding: 0, overflow: "hidden" }}
      >
        {/* Compact polaroid header — 1:1 avatar + name + tagline */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "104px minmax(0, 1fr)",
            gap: 14,
            padding: "16px 18px",
            alignItems: "center",
            background: "#FBFAFD",
            borderBottom: "1px solid #ECEBF1",
          }}
        >
          <div
            style={{
              width: 104,
              height: 104,
              borderRadius: 8,
              backgroundColor: "#EEEDF3",
              backgroundImage: photo ? `url(${photo})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              border: "1px solid #D9D8E4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Architect photo"
          >
            {!photo && (
              <span
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  color: "#9AA0C0",
                  fontSize: 11,
                  fontStyle: "italic",
                }}
              >
                No photo
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 18,
                color: "#1B1F4B",
                lineHeight: 1.2,
              }}
            >
              {draft.firm_name || draft.name || "Your firm"}
            </div>
            {draft.firm_tagline && (
              <div
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#6C6B85",
                  fontSize: 13,
                  marginTop: 4,
                  lineHeight: 1.45,
                }}
              >
                {draft.firm_tagline}
              </div>
            )}
            <span
              style={{
                marginTop: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                backgroundColor: "#B14E2F",
                color: "#FBFAFD",
                padding: "3px 9px",
                borderRadius: 999,
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 600,
                fontSize: 10.5,
              }}
            >
              <BadgeCheck size={11} />
              Verified Network Architect
            </span>
          </div>
        </div>

        {firmPhoto && (
          <div
            style={{
              aspectRatio: "4 / 3",
              backgroundImage: `url(${firmPhoto})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderBottom: "1px solid #ECEBF1",
            }}
            aria-label="Firm hero photo"
          />
        )}

        <div style={{ padding: "16px 18px" }}>
          {(draft.demographic_flags || []).length > 0 && (
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {draft.demographic_flags.map((f) => (
                <span
                  key={f}
                  style={{
                    backgroundColor: "#5B6196",
                    color: "#FBFAFD",
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: 999,
                  }}
                >
                  {f}
                </span>
              ))}
            </div>
          )}
          {draft.practice_statement && (
            <p
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#272636",
                fontSize: 15,
                lineHeight: 1.55,
              }}
            >
              {draft.practice_statement}
            </p>
          )}
          {draft.bio && (
            <p
              className="mt-3"
              style={{ color: "#272636", fontSize: 13.5, lineHeight: 1.6 }}
            >
              {draft.bio}
            </p>
          )}
          {(draft.service_areas || []).length > 0 && (
            <div className="mt-4">
              <div className="wn-label">Service areas</div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {draft.service_areas.map((s) => (
                  <span
                    key={s}
                    style={{
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "rgba(91,97,150,0.10)",
                      color: "#1B1F4B",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(draft.project_highlights || []).length > 0 && (
            <div className="mt-4">
              <div className="wn-label">Highlights</div>
              <ul className="mt-1 space-y-1">
                {draft.project_highlights.map((h, i) => (
                  <li
                    key={i}
                    style={{ fontSize: 13, color: "#272636" }}
                  >
                    <strong>{h.project_name || h.title || "Untitled"}</strong>
                    {(h.neighborhood || h.location) ? ` — ${h.neighborhood || h.location}` : ""}
                    {(h.completion_year || h.year) ? ` · ${h.completion_year || h.year}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChipRow({ options, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {options.map((opt) => {
        const isOn = selected.includes(opt.key);
        return (
          <button
            type="button"
            key={opt.key}
            onClick={() => onToggle(opt.key)}
            data-testid={`${testidPrefix}-${opt.key}`}
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: `1px solid ${isOn ? "#1B1F4B" : "#D9D8E4"}`,
              background: isOn ? "#1B1F4B" : "#FBFAFD",
              color: isOn ? "#FBFAFD" : "#272636",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function PhotoEditor({ value, onChange, testIdPrefix = "profile", aspect, width = 88 }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  async function onPick(file) {
    if (!file) return;
    setBusy(true);
    try {
      const meta = await uploadFile(file, "profile-photo");
      onChange(meta.url);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed.";
      toast.error(detail);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }
  const previewStyle = aspect
    ? {
        width,
        aspectRatio: aspect,
        borderRadius: 4,
        background: value
          ? `url(${assetUrl(value)}) center/cover no-repeat`
          : "#EEEDF3",
        border: "1px solid #D9D8E4",
        flexShrink: 0,
      }
    : {
        width,
        height: width,
        borderRadius: 10,
        background: value
          ? `url(${assetUrl(value)}) center/cover no-repeat`
          : "#EEEDF3",
        border: "1px solid #D9D8E4",
        flexShrink: 0,
      };
  return (
    <div className="mt-2 flex items-center gap-3">
      <div style={previewStyle} data-testid={`${testIdPrefix}-preview`} />
      <div>
        <button
          type="button"
          className="wn-btn wn-btn-outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          <Upload size={14} />
          {busy ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
        </button>
        {value && (
          <button
            type="button"
            className="wn-btn wn-btn-tertiary ml-2"
            onClick={() => onChange("")}
            data-testid={`${testIdPrefix}-clear-btn`}
            style={{ color: "#A23B2E" }}
          >
            <X size={12} /> Remove
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          onChange={(e) => onPick(e.target.files?.[0])}
        />
        <div
          className="mt-1"
          style={{ fontSize: 11, color: "#6C6B85" }}
        >
          JPG or PNG · up to 10 MB.
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   TagEditor — free-form chip-style array editor used for
   lifestyle_focuses (no canonical enum). Enter or comma commits a
   tag; Backspace on the empty field removes the last tag.
   ──────────────────────────────────────────────────────────────── */
function TagEditor({ tags, onChange, placeholder, max, testid }) {  const [draft, setDraft] = React.useState("");
  const inputRef = useRef(null);
  function commit() {
    const v = draft.trim();
    if (!v) return;
    if (tags.includes(v)) {
      setDraft("");
      return;
    }
    if (max && tags.length >= max) {
      toast.error(`You can add up to ${max} entries.`);
      return;
    }
    onChange([...tags, v]);
    setDraft("");
  }
  function remove(i) {
    onChange(tags.filter((_, idx) => idx !== i));
  }
  return (
    <div
      className="mt-2 wn-card"
      style={{ padding: "10px 12px" }}
      data-testid={`tag-editor-${testid}`}
    >
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {tags.map((t, i) => (
          <span
            key={`${t}-${i}`}
            data-testid={`tag-editor-${testid}-tag-${i}`}
            style={{
              padding: "4px 10px 4px 12px",
              borderRadius: 999,
              background: "#1B1F4B",
              color: "#FBFAFD",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.02em",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              lineHeight: 1.25,
            }}
          >
            {t}
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label={`Remove ${t}`}
              data-testid={`tag-editor-${testid}-remove-${i}`}
              style={{
                background: "transparent",
                color: "rgba(251,250,253,0.85)",
                border: "none",
                cursor: "pointer",
                padding: 0,
                lineHeight: 0,
              }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          placeholder={tags.length === 0 ? placeholder : "Add another…"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            } else if (e.key === "Backspace" && !draft && tags.length > 0) {
              onChange(tags.slice(0, -1));
            }
          }}
          onBlur={() => draft && commit()}
          data-testid={`tag-editor-${testid}-input`}
          style={{
            flex: "1 1 160px",
            minWidth: 160,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: '"Nunito Sans", sans-serif',
            fontSize: 13,
            color: "#272636",
            padding: "4px 2px",
          }}
        />
      </div>
      <div
        className="tabular-nums mt-1"
        style={{ fontSize: 11, color: "#6C6B85" }}
      >
        {tags.length}
        {max ? ` / ${max}` : ""} · Press Enter or comma to add.
      </div>
    </div>
  );
}

export function HighlightsEditor({ highlights, onChange, designs }) {
  const emptyHighlight = {
    project_name: "",
    neighborhood: "",
    lot_dimensions: "",
    narrative: "",
    completion_year: "",
    client_type: "",
    built_from_design_id: "",
    built_from_design_name: "",
    image_ref: "",
    metrics: [],
    _open: true,
  };
  function update(i, patch) {
    onChange(highlights.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  }
  function remove(i) {
    onChange(highlights.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...(highlights || []), { ...emptyHighlight }]);
  }
  function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= highlights.length) return;
    const copy = [...highlights];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  }
  function toggleOpen(i) {
    update(i, { _open: !highlights[i]._open });
  }
  return (
    <div className="mt-2 space-y-3" data-testid="highlights-editor">
      {highlights.length === 0 && (
        <div
          data-testid="highlights-empty"
          style={{ color: "#6C6B85", fontSize: 13, fontStyle: "italic", paddingTop: 4 }}
        >
          No completed projects added yet. Add your first case study below.
        </div>
      )}
      {highlights.map((h, i) => (
        <HighlightCard
          key={i}
          index={i}
          h={h}
          isOpen={h._open !== false}
          designs={designs}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
          onMoveUp={i > 0 ? () => move(i, -1) : null}
          onMoveDown={i < highlights.length - 1 ? () => move(i, 1) : null}
          onToggle={() => toggleOpen(i)}
        />
      ))}
      <button
        type="button"
        className="wn-btn wn-btn-outline"
        onClick={add}
        data-testid="highlights-add-btn"
      >
        <Plus size={14} /> Add a completed project
      </button>
    </div>
  );
}

function HighlightCard({
  index, h, isOpen, designs, onChange, onRemove, onMoveUp, onMoveDown, onToggle,
}) {
  return (
    <div
      className="wn-card"
      style={{ padding: 14 }}
      data-testid={`highlight-${index}`}
    >
      <div className="flex items-start gap-3">
        {/* Thumb */}
        <div
          style={{
            width: 64,
            height: 48,
            borderRadius: 4,
            border: "1px solid #ECEBF1",
            flexShrink: 0,
            background: h.image_ref
              ? `url(${assetUrl(h.image_ref)}) center/cover no-repeat`
              : "#EEEDF3",
          }}
        />
        {/* Title row */}
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 15,
              color: "#1B1F4B",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {h.project_name || "Untitled project"}
            {h.completion_year && (
              <span
                className="tabular-nums"
                style={{
                  marginLeft: 8,
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  fontWeight: 400,
                  color: "#6C6B85",
                  fontSize: 13,
                }}
              >
                {h.completion_year}
              </span>
            )}
          </div>
          <div style={{ color: "#6C6B85", fontSize: 12, marginTop: 2 }}>
            {[h.neighborhood, h.lot_dimensions, h.client_type]
              .filter(Boolean)
              .join(" · ") || "Details below"}
          </div>
        </div>
        <div className="flex items-center" style={{ gap: 4, flexShrink: 0 }}>
          <IconBtn
            label="Move up"
            disabled={!onMoveUp}
            onClick={onMoveUp || (() => {})}
            testid={`highlight-${index}-up`}
          ><ChevronUp size={14} /></IconBtn>
          <IconBtn
            label="Move down"
            disabled={!onMoveDown}
            onClick={onMoveDown || (() => {})}
            testid={`highlight-${index}-down`}
          ><ChevronDown size={14} /></IconBtn>
          <IconBtn
            label={isOpen ? "Collapse" : "Expand"}
            onClick={onToggle}
            testid={`highlight-${index}-toggle`}
          >{isOpen ? "—" : "+"}</IconBtn>
          <IconBtn
            label="Remove"
            onClick={onRemove}
            testid={`highlight-${index}-remove`}
            danger
          ><X size={14} /></IconBtn>
        </div>
      </div>

      {isOpen && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <Field label="Project name" required>
            <input
              className="wn-input"
              value={h.project_name}
              onChange={(e) => onChange({ project_name: e.target.value })}
              data-testid={`highlight-${index}-project-name`}
              placeholder="e.g. Eastside Twin"
              maxLength={80}
            />
          </Field>
          <Field label="Neighborhood">
            <input
              className="wn-input"
              value={h.neighborhood}
              onChange={(e) => onChange({ neighborhood: e.target.value })}
              data-testid={`highlight-${index}-neighborhood`}
              placeholder="e.g. Near Eastside"
              maxLength={80}
            />
          </Field>
          <Field label="Lot dimensions">
            <input
              className="wn-input tabular-nums"
              value={h.lot_dimensions}
              onChange={(e) => onChange({ lot_dimensions: e.target.value })}
              data-testid={`highlight-${index}-lot`}
              placeholder='40′ × 130′'
              maxLength={40}
            />
          </Field>
          <Field label="Completion year">
            <input
              className="wn-input tabular-nums"
              value={h.completion_year}
              inputMode="numeric"
              onChange={(e) => onChange({
                completion_year: e.target.value.replace(/[^\d]/g, "").slice(0, 4),
              })}
              data-testid={`highlight-${index}-year`}
              placeholder="e.g. 2024"
            />
          </Field>
          <Field label="Client type">
            <input
              className="wn-input"
              value={h.client_type}
              onChange={(e) => onChange({ client_type: e.target.value })}
              data-testid={`highlight-${index}-client`}
              placeholder="e.g. CDC owner-builder"
              maxLength={80}
            />
          </Field>
          <Field label="Built from">
            <select
              className="wn-input"
              value={h.built_from_design_id || ""}
              onChange={(e) => {
                const id = e.target.value;
                const d = designs.find((x) => x.id === id);
                onChange({
                  built_from_design_id: id,
                  built_from_design_name: d?.name || "",
                });
              }}
              data-testid={`highlight-${index}-design`}
            >
              <option value="">— none —</option>
              {designs.map((d) => (
                <option key={d.id} value={d.id}>{d.name || d.id}</option>
              ))}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Narrative">
              <textarea
                className="wn-input"
                value={h.narrative}
                onChange={(e) => onChange({ narrative: e.target.value })}
                data-testid={`highlight-${index}-narrative`}
                placeholder="2–3 sentences on the project — what made it sing, what you'd do again."
                maxLength={400}
                style={{ minHeight: 80, resize: "vertical" }}
              />
              <div style={{ color: "#6C6B85", fontSize: 11, marginTop: 4 }} className="tabular-nums">
                {(h.narrative || "").length}/400
              </div>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Hero image">
              <PhotoEditor
                value={h.image_ref}
                onChange={(url) => onChange({ image_ref: url })}
                testIdPrefix={`highlight-${index}-image`}
                aspect="16 / 10"
                width={160}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Quick metrics (optional)">
              <MetricsEditor
                metrics={h.metrics || []}
                onChange={(arr) => onChange({ metrics: arr })}
                testid={`highlight-${index}-metrics`}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="wn-label" style={{ marginBottom: 4 }}>
        {label}
        {required && (
          <span style={{ color: "#B14E2F", marginLeft: 4 }}>*</span>
        )}
      </div>
      {children}
    </div>
  );
}

function IconBtn({ children, onClick, label, testid, disabled, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
      data-testid={testid}
      style={{
        width: 26,
        height: 26,
        borderRadius: 4,
        border: "1px solid #D9D8E4",
        background: "#FBFAFD",
        color: disabled ? "#9AA0C0" : danger ? "#A23B2E" : "#272636",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        fontSize: 14,
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

function MetricsEditor({ metrics, onChange, testid }) {
  function update(i, patch) {
    onChange(metrics.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  function remove(i) {
    onChange(metrics.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...metrics, { label: "", value: "" }]);
  }
  return (
    <div data-testid={`metrics-${testid}`}>
      {metrics.length === 0 && (
        <div style={{ color: "#6C6B85", fontSize: 12, fontStyle: "italic", padding: "2px 0 6px" }}>
          Optional. Add up to 3 quick metrics (e.g. "Heating cost / yr · $640").
        </div>
      )}
      <div className="space-y-2">
        {metrics.map((m, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className="wn-input"
              value={m.label || ""}
              placeholder="Label (e.g. Heating cost / yr)"
              onChange={(e) => update(i, { label: e.target.value })}
              data-testid={`metrics-${testid}-${i}-label`}
              maxLength={50}
              style={{ flex: "1 1 220px" }}
            />
            <input
              className="wn-input tabular-nums"
              value={m.value || ""}
              placeholder="Value (e.g. $640)"
              onChange={(e) => update(i, { value: e.target.value })}
              data-testid={`metrics-${testid}-${i}-value`}
              maxLength={30}
              style={{ flex: "1 1 140px" }}
            />
            <IconBtn
              label="Remove metric"
              onClick={() => remove(i)}
              testid={`metrics-${testid}-${i}-remove`}
              danger
            >
              <X size={12} />
            </IconBtn>
          </div>
        ))}
      </div>
      {metrics.length < 3 && (
        <button
          type="button"
          className="wn-btn wn-btn-tertiary mt-2"
          onClick={add}
          data-testid={`metrics-${testid}-add`}
          style={{ paddingLeft: 0 }}
        >
          <Plus size={12} /> Add metric
        </button>
      )}
    </div>
  );
}

export function CredentialsBlockEditor({ label, rows, schema, onChange, emptyTemplate, testid }) {
  function update(i, key, value) {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  }
  function remove(i) {
    onChange(rows.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...rows, { ...emptyTemplate }]);
  }
  return (
    <div className="mt-4" data-testid={`credentials-block-${testid}`}>
      <div
        style={{
          color: "#272636",
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: "0.02em",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div className="space-y-2">
        {rows.length === 0 && (
          <div
            style={{ color: "#6C6B85", fontSize: 12.5, fontStyle: "italic", padding: "4px 0" }}
            data-testid={`credentials-empty-${testid}`}
          >
            None yet.
          </div>
        )}
        {rows.map((r, i) => (
          <div
            key={i}
            className="wn-card"
            style={{ padding: "10px 12px" }}
            data-testid={`credentials-row-${testid}-${i}`}
          >
            <div className="flex flex-wrap gap-2">
              {schema.map((col) => {
                const value = r[col.key] ?? "";
                const common = {
                  className: `wn-input${col.tabular ? " tabular-nums" : ""}`,
                  placeholder: col.placeholder,
                  value,
                  onChange: (e) =>
                    update(
                      i,
                      col.key,
                      col.type === "number"
                        ? e.target.value === "" ? "" : Number(e.target.value)
                        : e.target.value,
                    ),
                  style: { flex: `${col.grow} 1 ${col.grow * 60}px`, minWidth: 100 },
                  "data-testid": `credentials-input-${testid}-${i}-${col.key}`,
                };
                if (col.multiline) {
                  return <textarea key={col.key} rows={2} {...common} />;
                }
                if (col.type === "number") {
                  return <input key={col.key} type="number" {...common} />;
                }
                return <input key={col.key} type="text" {...common} />;
              })}
            </div>
            <button
              type="button"
              className="wn-btn wn-btn-tertiary mt-1"
              onClick={() => remove(i)}
              style={{ paddingLeft: 0, color: "#A23B2E" }}
              data-testid={`credentials-remove-${testid}-${i}`}
            >
              <X size={12} /> Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="wn-btn wn-btn-outline"
          onClick={add}
          data-testid={`credentials-add-${testid}`}
        >
          <Plus size={14} /> Add {label.toLowerCase().replace(/ \(.*?\)/, "").replace(/s$/, "")}
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   VerificationRequiredFields — separate sub-form for license_no
   and years_practising. Submissions don't go live until an admin
   reviews; the architect sees a pending banner with the proposed
   values while review is outstanding.
   ──────────────────────────────────────────────────────────────── */
function VerificationRequiredFields({ profile, onSubmitted }) {
  const { user } = useRole();
  const pending = profile.pending_verification || {};
  const hasPending = Object.keys(pending).some(
    (k) => k !== "requested_at" && k !== "note" && pending[k] != null,
  );
  const [open, setOpen] = useState(false);
  const [licenseNo, setLicenseNo] = useState(profile.license_no || "");
  const [yearsPracticing, setYearsPracticing] = useState(
    profile.years_practising == null ? "" : String(profile.years_practising),
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const dirty =
    (licenseNo.trim() !== (profile.license_no || "").trim()) ||
    (yearsPracticing !== "" &&
      Number(yearsPracticing) !== (profile.years_practising ?? null));

  async function onSubmit() {
    if (!dirty) return;
    setBusy(true);
    try {
      const fields = {};
      if (licenseNo.trim() !== (profile.license_no || "").trim()) {
        fields.license_no = licenseNo.trim();
      }
      if (yearsPracticing !== "" &&
          Number(yearsPracticing) !== (profile.years_practising ?? null)) {
        fields.years_practising = Number(yearsPracticing);
      }
      if (note.trim()) fields.note = note.trim();
      await requestVerification(user.id, fields);
      toast.success("Submitted for review. An admin will confirm shortly.");
      setOpen(false);
      setNote("");
      onSubmitted();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not submit.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt-6"
      data-testid="verification-section"
      style={{
        border: "1px solid #D9D8E4",
        borderLeft: "3px solid #4F6149",
        borderRadius: 4,
        padding: 18,
        background: "rgba(79,97,73,0.04)",
      }}
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} color="#4F6149" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div className="wn-label" style={{ color: "#4F6149" }}>
              Verification-gated credentials
            </div>
            <div
              className="mt-1"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 16,
                color: "#1B1F4B",
              }}
            >
              License & years practising
            </div>
            <p
              style={{
                color: "#6C6B85",
                fontSize: 12.5,
                lineHeight: 1.5,
                marginTop: 2,
                maxWidth: 480,
              }}
            >
              Changes to these fields don't go live immediately — an
              administrator reviews each update before it's published.
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="wn-label">Currently on file</div>
          <div
            className="tabular-nums"
            style={{ color: "#272636", fontSize: 13, marginTop: 2 }}
          >
            License <strong>{profile.license_no || "—"}</strong>
          </div>
          <div
            className="tabular-nums"
            style={{ color: "#272636", fontSize: 13, marginTop: 2 }}
          >
            Practising <strong>{profile.years_practising ?? "—"}</strong> yr
          </div>
        </div>
      </div>

      {hasPending && (
        <div
          className="mt-4 wn-card"
          style={{
            padding: 12,
            background: "rgba(177,78,47,0.06)",
            border: "1px solid rgba(177,78,47,0.30)",
          }}
          data-testid="verification-pending"
        >
          <div className="wn-label" style={{ color: "#8F3C23" }}>
            Pending admin review
          </div>
          <div className="tabular-nums" style={{ fontSize: 13, color: "#272636", marginTop: 4 }}>
            {pending.license_no != null && (
              <div>License → <strong>{pending.license_no}</strong></div>
            )}
            {pending.years_practising != null && (
              <div>Practising → <strong>{pending.years_practising}</strong> yr</div>
            )}
            {pending.requested_at && (
              <div style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 4, fontStyle: "italic" }}>
                Submitted {new Date(pending.requested_at).toLocaleString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {!open ? (
        <button
          type="button"
          className="wn-btn wn-btn-outline mt-4"
          onClick={() => setOpen(true)}
          data-testid="verification-open"
          disabled={hasPending}
          title={hasPending ? "An update is already pending review" : undefined}
        >
          {hasPending ? "Update under review" : "Request an update"}
        </button>
      ) : (
        <div className="mt-4 space-y-3" data-testid="verification-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="wn-label">License number</div>
              <input
                className="wn-input mt-1 tabular-nums"
                value={licenseNo}
                onChange={(e) => setLicenseNo(e.target.value)}
                maxLength={40}
                data-testid="verification-license"
              />
            </div>
            <div>
              <div className="wn-label">Years practising</div>
              <input
                className="wn-input mt-1 tabular-nums"
                inputMode="numeric"
                value={yearsPracticing}
                onChange={(e) =>
                  setYearsPracticing(
                    e.target.value.replace(/[^\d]/g, "").slice(0, 2),
                  )
                }
                data-testid="verification-years"
              />
            </div>
          </div>
          <div>
            <div className="wn-label">Note to the reviewer (optional)</div>
            <textarea
              className="wn-input mt-1"
              style={{ minHeight: 70, resize: "vertical" }}
              placeholder="e.g. License renewed Feb 2026 — new number is 32100099."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={400}
              data-testid="verification-note"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="wn-btn wn-btn-tertiary"
              onClick={() => { setOpen(false); setNote(""); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="wn-btn wn-btn-primary"
              onClick={onSubmit}
              disabled={!dirty || busy}
              data-testid="verification-submit"
            >
              {busy ? "Submitting…" : "Submit for review"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────
   LastAdminEditBanner — surfaces the most recent admin proxy-edit
   so the architect can see what changed and when. Closes the trust
   loop on the audit-logged proxy-edit flow.
   ──────────────────────────────────────────────────────────────── */
function LastAdminEditBanner({ edit }) {
  if (!edit) return null;
  const when = (() => {
    if (!edit.at) return "recently";
    try {
      return new Date(edit.at).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return edit.at; }
  })();
  const fields = (edit.fields || []).filter((f) =>
    f !== "updated_at" && f !== "pending_verification"
  );
  return (
    <div
      className="mt-5 wn-card"
      data-testid="last-admin-edit-banner"
      style={{
        padding: "12px 16px",
        borderLeft: "3px solid #5B6196",
        background: "rgba(91,97,150,0.06)",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <ShieldAlert size={18} color="#5B6196" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          className="wn-label"
          style={{ color: "#5B6196", letterSpacing: "0.08em" }}
        >
          Last admin update
        </div>
        <div
          className="mt-1"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            color: "#1B1F4B",
            fontSize: 15.5,
            lineHeight: 1.35,
          }}
        >
          <strong style={{ fontWeight: 600 }}>{edit.actor_name}</strong>{" "}
          updated your profile{" "}
          <span
            className="tabular-nums"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              fontWeight: 400,
              color: "#6C6B85",
            }}
          >
            on {when}
          </span>
          .
        </div>
        {fields.length > 0 && (
          <FieldChips fields={fields} />
        )}
        {edit.reason && (
          <div
            className="mt-2"
            style={{
              color: "#272636",
              fontSize: 13,
              lineHeight: 1.5,
              borderLeft: "2px solid #D9D8E4",
              paddingLeft: 10,
              fontStyle: "italic",
              fontFamily: '"Newsreader", Georgia, serif',
            }}
          >
            "{edit.reason}"
          </div>
        )}
      </div>
    </div>
  );
}

function FieldChips({ fields }) {
  const [open, setOpen] = useState(false);
  const visible = open ? fields : fields.slice(0, 5);
  return (
    <div className="mt-2 flex flex-wrap items-center" style={{ gap: 6 }}>
      <span
        className="wn-label"
        style={{ color: "#6C6B85", alignSelf: "center" }}
      >
        Fields:
      </span>
      {visible.map((f) => (
        <span
          key={f}
          data-testid={`last-admin-edit-field-${f}`}
          style={{
            padding: "2px 9px",
            borderRadius: 999,
            background: "rgba(91,97,150,0.10)",
            color: "#5B6196",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.03em",
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {f.replace(/_/g, " ")}
        </span>
      ))}
      {fields.length > 5 && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="last-admin-edit-toggle"
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            color: "#5B6196",
            fontSize: 11,
            fontWeight: 700,
            fontFamily: '"Nunito Sans", sans-serif',
            letterSpacing: "0.03em",
            cursor: "pointer",
          }}
        >
          {open ? "show less" : `+${fields.length - 5} more`}
        </button>
      )}
    </div>
  );
}
